<template>
  <v-card outlined class="mb-6">
    <v-card-title class="text-h6">
      <v-icon left>mdi-image-multiple-outline</v-icon>
      App-Icons generieren
    </v-card-title>
    <v-card-text>
      <p class="text-body-2 mb-4">
        Lade ein quadratisches Logo (am besten <b>.svg</b>, sonst ein großes
        PNG). Alle benötigten Favicon- und App-Icons werden im Browser erzeugt
        und unter <code>store/AppData/</code> abgelegt (updatesicher).
      </p>

      <v-file-input
        v-model="datei"
        label="Logo auswählen (.svg / .png)"
        accept=".svg,image/svg+xml,image/png,image/jpeg"
        prepend-icon="mdi-file-image-outline"
        show-size
        clearable
        :disabled="loading"
        @change="quelleGeladen();"
      ></v-file-input>

      <v-row v-if="vorschauUrl" align="center" class="mb-2">
        <v-col cols="auto">
          <img :src="vorschauUrl" alt="Vorschau"
            style="width:72px;height:72px;object-fit:contain;border:1px solid #ddd;border-radius:8px;"
            :style="transparent ? '' : `background:${bgColor}`" />
        </v-col>
        <v-col>
          <v-checkbox
            v-model="transparent"
            label="Hintergrund transparent lassen"
            hint="Aus für App-Icons (Android/iOS) meist besser einen Hintergrund setzen"
            persistent-hint
            dense
          ></v-checkbox>
          <div v-if="!transparent" class="d-flex align-center mt-1">
            <span class="text-body-2 mr-2">Hintergrundfarbe:</span>
            <input type="color" v-model="bgColor" style="width:40px;height:28px;border:none;background:none;">
            <span class="text-caption ml-2">{{ bgColor }}</span>
          </div>
        </v-col>
      </v-row>

      <v-btn
        color="primary"
        :loading="loading"
        :disabled="!datei || loading"
        @click="generierenUndSpeichern();"
      >
        <v-icon left>mdi-cog-play-outline</v-icon>
        Icons generieren &amp; speichern
      </v-btn>

      <v-alert v-if="ergebnis" type="success" text border="left" class="mt-4">
        <b>{{ ergebnis.length }} Icons erzeugt und gespeichert.</b>
        <div class="mt-2 text-caption">
          Lade die App neu (ggf. Service-Worker in DevTools „Unregister"),
          damit die neuen Icons erscheinen.
          <div class="mt-2">
            <v-btn small color="primary" @click="neuLaden();">App neu laden</v-btn>
          </div>
        </div>
      </v-alert>
      <v-alert v-if="fehler" type="error" text border="left" class="mt-4">{{ fehler }}</v-alert>
    </v-card-text>
  </v-card>
</template>

<script>
// Zielgrößen (relativ zu store/AppData/). bg=true -> Hintergrund füllen,
// pad -> Sicherheitsrand (für maskable-Icons).
const ZIELE = [
  { path: 'icons/favicon-16x16.png', size: 16, bg: false },
  { path: 'icons/favicon-32x32.png', size: 32, bg: false },
  { path: 'icons/android-chrome-192x192.png', size: 192, bg: true },
  { path: 'icons/android-chrome-512x512.png', size: 512, bg: true },
  { path: 'icons/android-chrome-maskable-192x192.png', size: 192, bg: true, pad: 0.1 },
  { path: 'icons/android-chrome-maskable-512x512.png', size: 512, bg: true, pad: 0.1 },
  { path: 'icons/apple-touch-icon.png', size: 180, bg: true },
  { path: 'icons/apple-touch-icon-60x60.png', size: 60, bg: true },
  { path: 'icons/apple-touch-icon-76x76.png', size: 76, bg: true },
  { path: 'icons/apple-touch-icon-120x120.png', size: 120, bg: true },
  { path: 'icons/apple-touch-icon-152x152.png', size: 152, bg: true },
  { path: 'icons/apple-touch-icon-180x180.png', size: 180, bg: true },
  { path: 'icons/msapplication-icon-144x144.png', size: 144, bg: true },
  { path: 'icons/mstile-150x150.png', size: 150, bg: true },
];

export default {
  name: 'IconGenerator',
  data: () => ({
    datei: null,
    bgColor: '#ffffff',
    transparent: false,
    vorschauUrl: '',
    loading: false,
    ergebnis: null,
    fehler: '',
  }),
  computed: {
    istSvg() {
      return !!this.datei && (this.datei.type === 'image/svg+xml'
        || /\.svg$/i.test(this.datei.name || ''));
    },
  },
  methods: {
    quelleGeladen() {
      this.ergebnis = null;
      this.fehler = '';
      if (this.vorschauUrl) URL.revokeObjectURL(this.vorschauUrl);
      this.vorschauUrl = this.datei ? URL.createObjectURL(this.datei) : '';
    },
    ladeBild() {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Bild konnte nicht geladen werden.'));
        img.src = URL.createObjectURL(this.datei);
      });
    },
    zeichne(img, ziel) {
      return new Promise((resolve) => {
        const c = document.createElement('canvas');
        c.width = ziel.size;
        c.height = ziel.size;
        const ctx = c.getContext('2d');
        if (ziel.bg && !this.transparent) {
          ctx.fillStyle = this.bgColor;
          ctx.fillRect(0, 0, ziel.size, ziel.size);
        }
        const iw = img.naturalWidth || img.width || ziel.size;
        const ih = img.naturalHeight || img.height || ziel.size;
        const pad = ziel.pad || 0;
        const verf = ziel.size * (1 - 2 * pad);
        const scale = Math.min(verf / iw, verf / ih);
        const w = iw * scale;
        const h = ih * scale;
        ctx.drawImage(img, (ziel.size - w) / 2, (ziel.size - h) / 2, w, h);
        c.toBlob((b) => resolve(b), 'image/png');
      });
    },
    async baueIco(pngBuffers) {
      // pngBuffers: [{ size, bytes: ArrayBuffer }]
      const count = pngBuffers.length;
      const headerSize = 6 + count * 16;
      const total = pngBuffers.reduce((s, p) => s + p.bytes.byteLength, 0);
      const buf = new ArrayBuffer(headerSize + total);
      const dv = new DataView(buf);
      dv.setUint16(0, 0, true);
      dv.setUint16(2, 1, true);
      dv.setUint16(4, count, true);
      let dir = 6;
      let dataOff = headerSize;
      pngBuffers.forEach((p) => {
        dv.setUint8(dir + 0, p.size >= 256 ? 0 : p.size);
        dv.setUint8(dir + 1, p.size >= 256 ? 0 : p.size);
        dv.setUint8(dir + 2, 0);
        dv.setUint8(dir + 3, 0);
        dv.setUint16(dir + 4, 1, true);
        dv.setUint16(dir + 6, 32, true);
        dv.setUint32(dir + 8, p.bytes.byteLength, true);
        dv.setUint32(dir + 12, dataOff, true);
        dir += 16;
        new Uint8Array(buf, dataOff, p.bytes.byteLength).set(new Uint8Array(p.bytes));
        dataOff += p.bytes.byteLength;
      });
      return new Blob([buf], { type: 'image/x-icon' });
    },
    async generierenUndSpeichern() {
      this.loading = true;
      this.ergebnis = null;
      this.fehler = '';
      try {
        const img = await this.ladeBild();
        const dateien = [];
        // Alle PNG-Größen
        for (let i = 0; i < ZIELE.length; i += 1) {
          // eslint-disable-next-line no-await-in-loop
          const blob = await this.zeichne(img, ZIELE[i]);
          dateien.push({ path: ZIELE[i].path, blob });
        }
        // favicon.ico aus 16 + 32
        const ico16 = await this.zeichne(img, { size: 16, bg: false });
        const ico32 = await this.zeichne(img, { size: 32, bg: false });
        const ico = await this.baueIco([
          { size: 16, bytes: await ico16.arrayBuffer() },
          { size: 32, bytes: await ico32.arrayBuffer() },
        ]);
        dateien.push({ path: 'favicon.ico', blob: ico });
        // SVG-Quellen durchreichen (falls SVG hochgeladen)
        if (this.istSvg) {
          dateien.push({ path: 'icons/favicon.svg', blob: this.datei });
          dateien.push({ path: 'icons/safari-pinned-tab.svg', blob: this.datei });
        }

        const body = new FormData();
        body.append('code', this.$store.state.login.session.code);
        dateien.forEach((d) => {
          body.append('paths[]', d.path);
          body.append('files[]', d.blob, d.path.split('/').pop());
        });
        const res = await fetch(`${process.env.VUE_APP_API_HOST}/000/001/saveIcons.php`, {
          method: 'POST',
          body,
        }).then((r) => r.json());
        if (res && res.status === 'success') {
          this.ergebnis = res.gespeichert || [];
        } else {
          this.fehler = (res && res.statusInfo) ? res.statusInfo : 'Speichern fehlgeschlagen.';
        }
      } catch (e) {
        this.fehler = `Fehler: ${e.message || e}`;
      }
      this.loading = false;
    },
    neuLaden() {
      window.location.reload();
    },
  },
  beforeDestroy() {
    if (this.vorschauUrl) URL.revokeObjectURL(this.vorschauUrl);
  },
};
</script>
